/**
 * How strongly one part of the /how-it-works crawl reads, from the distance
 * of its pair to the pair in focus, in pairs (#2810). The two parts in focus
 * read at full strength. Every other part fades out as it leaves, so only
 * two parts show at a time, and the parts to come never meet the speech
 * bubbles under the words.
 */
export function crawlStrength(distance: number): number {
  const d = Math.abs(distance);
  if (d >= 1) return 0;
  // Smoothstep, so a part fades as it glides off the focus line instead of
  // dropping at once.
  return 1 - d * d * (3 - 2 * d);
}

/**
 * How strongly a chapter block shows at `distance` chapters from the one in
 * view. The old block is gone by half way and the new one starts there, so
 * two blocks never show over each other (#2809).
 */
export function chapterStrength(distance: number): number {
  return crawlStrength(Math.min(1, Math.abs(distance) * 2));
}

/**
 * The sentence the words show (#2809). `raw` is the scroll position in
 * sentences (2.5 is half way from sentence 2 to 3), `aim` the sentence shown
 * now, `moved` the change of `raw` since the last frame, `chapter` the
 * chapter in view (from the marker in the middle of the screen) and
 * `lineChapter` the chapter of each sentence.
 *
 * Only a move changes the sentence, in its own direction, so a rest half way
 * never flips between two. The sentence always belongs to the chapter in
 * view: a jump to a chapter, or a rest on its title, shows that chapter's
 * first sentence (from above) or its last one (from below).
 */
export function aimAt(
  raw: number,
  aim: number,
  moved: number,
  chapter: number,
  lineChapter: ReadonlyArray<number>,
): number {
  let next = aim < 0 ? Math.round(raw) : aim;
  if (aim >= 0 && moved > 0 && raw > aim + 0.05) next = Math.ceil(raw - 0.05);
  else if (aim >= 0 && moved < 0 && raw < aim - 0.05) next = Math.floor(raw + 0.05);
  const at = lineChapter[next];
  if (at === undefined || at === chapter) return next;
  const first = lineChapter.indexOf(chapter);
  const last = lineChapter.lastIndexOf(chapter);
  if (first < 0) return next;
  return chapter > at ? first : last;
}
